/**
 * Account billing (Task 13) — the CALLER'S OWN "me" billing surface: the
 * cross-organization summary, and the account's own invoices/charges.
 *
 * BE returns RAW responses (no `{ data }` wrapper) — see
 * `@mfw/common/types/accountBilling.ts`.
 */
import type {
  AccountBillingSummary,
  InvoiceListPage,
  InvoiceDetailDto,
  InvoiceDocumentDto,
  PayoutStatementPage,
  PayoutStatementDocumentDto,
  SubscriptionChargePage,
} from '@mfw/common';

import { get } from './axios';

export type FetchMyInvoicesArgs = { limit?: number; offset?: number };
export type FetchMyInvoiceArgs = { invoiceId: string };
export type FetchMyInvoiceDocumentArgs = { invoiceId: string };
export type FetchMyChargesArgs = { limit?: number; offset?: number };
// Q9/Task 8: a closed organization's own invoices, read via the last-owner
// historical-access route (`payments/me/organizations/:orgId/*`) — never the
// live `organizations/:id/billing/*` family, which 403s a closed organization
// by design.
export type FetchHistoricalOrgInvoicesArgs = {
  organizationId: string;
  limit?: number;
  offset?: number;
};
export type FetchHistoricalOrgInvoiceArgs = {
  organizationId: string;
  invoiceId: string;
};
export type FetchHistoricalOrgInvoiceDocumentArgs = {
  organizationId: string;
  invoiceId: string;
};
// DD3: a closed organization's issued payout statements, through the same
// last-owner historical-access family as its invoices.
export type FetchHistoricalOrgPayoutStatementsArgs = {
  organizationId: string;
  limit?: number;
  offset?: number;
};
export type FetchHistoricalOrgPayoutStatementDocumentArgs = {
  organizationId: string;
  period: string;
  currency: string;
};

export const fetchAccountBillingSummary =
  async (): Promise<AccountBillingSummary> => {
    const response = await get<AccountBillingSummary>(
      '/v2/payments/me/billing-summary',
    );
    return response as AccountBillingSummary;
  };

export const fetchMyInvoices = async ({
  limit,
  offset,
}: FetchMyInvoicesArgs): Promise<InvoiceListPage> => {
  const response = await get<InvoiceListPage>('/v2/payments/me/invoices', {
    query: {
      limit: limit !== undefined ? String(limit) : undefined,
      offset: offset !== undefined ? String(offset) : undefined,
    },
  });
  return response ?? { data: [], total: 0 };
};

export const fetchMyInvoice = async ({
  invoiceId,
}: FetchMyInvoiceArgs): Promise<InvoiceDetailDto> => {
  const response = await get<InvoiceDetailDto>(
    `/v2/payments/me/invoices/${invoiceId}`,
  );
  return response as InvoiceDetailDto;
};

/**
 * Q5/Task 10 — my own invoice's PAYMENT CONFIRMATION document, as a
 * short-lived (120s) presigned URL. Fetch ONLY on click, never prefetched or
 * cached: a URL minted at render time may be dead by the time it is used, and
 * — because it is a bearer capability, readable by anyone holding it with no
 * session — it must never be reused from a query cache. See
 * `InvoiceDetailDialog.useInvoiceDocumentDownload`, the one place that calls
 * this.
 */
export const fetchMyInvoiceDocument = async ({
  invoiceId,
}: FetchMyInvoiceDocumentArgs): Promise<InvoiceDocumentDto> => {
  const response = await get<InvoiceDocumentDto>(
    `/v2/payments/me/invoices/${invoiceId}/document`,
  );
  return response as InvoiceDocumentDto;
};

export const fetchHistoricalOrgInvoices = async ({
  organizationId,
  limit,
  offset,
}: FetchHistoricalOrgInvoicesArgs): Promise<InvoiceListPage> => {
  const response = await get<InvoiceListPage>(
    `/v2/payments/me/organizations/${organizationId}/invoices`,
    {
      query: {
        limit: limit !== undefined ? String(limit) : undefined,
        offset: offset !== undefined ? String(offset) : undefined,
      },
    },
  );
  return response ?? { data: [], total: 0 };
};

export const fetchHistoricalOrgInvoice = async ({
  organizationId,
  invoiceId,
}: FetchHistoricalOrgInvoiceArgs): Promise<InvoiceDetailDto> => {
  const response = await get<InvoiceDetailDto>(
    `/v2/payments/me/organizations/${organizationId}/invoices/${invoiceId}`,
  );
  return response as InvoiceDetailDto;
};

/**
 * Q5/Task 10 — the payment confirmation for a CLOSED organization's own
 * invoice, read through the last-owner historical-access route. Note the URL
 * shape: the backend param there is deliberately `:orgId`, not
 * `:organizationId` — `SecurityGuard`'s organization precondition keys on the
 * literal param name and demands an ACTIVE org/membership, which a closed
 * organization fails by design. The frontend arg name below is just a name;
 * only the path segment matters.
 */
export const fetchHistoricalOrgInvoiceDocument = async ({
  organizationId,
  invoiceId,
}: FetchHistoricalOrgInvoiceDocumentArgs): Promise<InvoiceDocumentDto> => {
  const response = await get<InvoiceDocumentDto>(
    `/v2/payments/me/organizations/${organizationId}/invoices/${invoiceId}/document`,
  );
  return response as InvoiceDocumentDto;
};

/**
 * DD3 — a CLOSED organization's issued payout statements, newest month first.
 * Read-only by construction: this family has no issue route, and an archived
 * organization has no new months to state.
 */
export const fetchHistoricalOrgPayoutStatements = async ({
  organizationId,
  limit,
  offset,
}: FetchHistoricalOrgPayoutStatementsArgs): Promise<PayoutStatementPage> => {
  const response = await get<PayoutStatementPage>(
    `/v2/payments/me/organizations/${organizationId}/payout-statements`,
    {
      query: {
        limit: limit !== undefined ? String(limit) : undefined,
        offset: offset !== undefined ? String(offset) : undefined,
      },
    },
  );
  return response ?? { data: [], total: 0 };
};

/**
 * DD3 — a fresh, short-lived (120s) link to one of those statements' PDF.
 * The live organization's statement routes are NOT an alternative: their
 * guard demands an ACTIVE organization and membership, which a closed
 * organization fails by design (see `fetchHistoricalOrgInvoiceDocument`).
 */
export const fetchHistoricalOrgPayoutStatementDocument = async ({
  organizationId,
  period,
  currency,
}: FetchHistoricalOrgPayoutStatementDocumentArgs): Promise<PayoutStatementDocumentDto> => {
  const response = await get<PayoutStatementDocumentDto>(
    `/v2/payments/me/organizations/${organizationId}/payout-statements/${period}/${currency}/document`,
  );
  return response as PayoutStatementDocumentDto;
};

export type FetchHistoricalOrgPayoutStatementCsvArgs = {
  organizationId: string;
  period: string;
  currency: string;
};

/**
 * Task 3.1 — the CSV sibling of the historical PDF above, for a CLOSED
 * organization's last owners. Same blob-download shape as
 * `fetchOrgPayoutStatementCsv` (`marketplacePayments.api.ts`) — the backend
 * builds it fresh from the frozen row on every call, so there is no link to
 * mint here either.
 */
export const fetchHistoricalOrgPayoutStatementCsv = async ({
  organizationId,
  period,
  currency,
}: FetchHistoricalOrgPayoutStatementCsvArgs): Promise<Blob | null> => {
  const response = await get<Blob>(
    `/v2/payments/me/organizations/${organizationId}/payout-statements/${period}/${currency}/csv`,
    { responseType: 'blob' },
  );
  return response ?? null;
};

export const fetchMyCharges = async ({
  limit,
  offset,
}: FetchMyChargesArgs): Promise<SubscriptionChargePage> => {
  const response = await get<SubscriptionChargePage>(
    '/v2/payments/me/charges',
    {
      query: {
        limit: limit !== undefined ? String(limit) : undefined,
        offset: offset !== undefined ? String(offset) : undefined,
      },
    },
  );
  return response ?? { data: [], total: 0 };
};
